import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Eye, EyeOff, Plus, Trash2, Upload, Building2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { useToast } from '@/hooks/use-toast';
import type { TumUtilityVendor } from '@/types/tum';

interface SecurityQuestion {
  question: string;
  answer: string;
}

interface VendorFormModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (vendor: Partial<TumUtilityVendor>) => void;
  vendor: TumUtilityVendor | null;
  isLoading: boolean;
}

export const VendorFormModal = ({ open, onClose, onSave, vendor, isLoading }: VendorFormModalProps) => {
  const { currentWorkspace } = useWorkspace();
  const { toast } = useToast();
  const [showPassword, setShowPassword] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    vendor_name: '',
    vendor_code: '',
    portal_url: '',
    portal_username: '',
    portal_password_encrypted: '',
    customer_service_phone: '',
    customer_service_email: '',
    website_url: '',
    logo_url: '',
    is_active: true,
    notes: '',
    security_questions: [] as SecurityQuestion[],
    service_regions: [] as string[],
    payment_methods: [] as string[],
  });

  // Reset form when vendor changes
  useEffect(() => {
    if (vendor) {
      setFormData({
        vendor_name: vendor.vendor_name || '',
        vendor_code: vendor.vendor_code || '',
        portal_url: vendor.portal_url || '',
        portal_username: vendor.portal_username || '',
        portal_password_encrypted: vendor.portal_password_encrypted || '',
        customer_service_phone: vendor.customer_service_phone || '',
        customer_service_email: vendor.customer_service_email || '',
        website_url: vendor.website_url || '',
        logo_url: vendor.logo_url || '',
        is_active: vendor.is_active !== false,
        notes: vendor.notes || '',
        security_questions: (vendor.security_questions as unknown as SecurityQuestion[]) || [],
        service_regions: (vendor.service_regions as unknown as string[]) || [],
        payment_methods: (vendor.payment_methods as unknown as string[]) || [],
      });
    } else {
      // Reset for new vendor
      setFormData({
        vendor_name: '',
        vendor_code: '',
        portal_url: '',
        portal_username: '',
        portal_password_encrypted: '',
        customer_service_phone: '',
        customer_service_email: '',
        website_url: '',
        logo_url: '',
        is_active: true,
        notes: '',
        security_questions: [],
        service_regions: [],
        payment_methods: [],
      });
    }
  }, [vendor, open]);

  const updateField = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Handle logo upload
  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !currentWorkspace) return;

    setUploadingLogo(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${currentWorkspace.id}/${Date.now()}.${fileExt}`;
      
      const { data, error } = await supabase.storage
        .from('vendor-logos')
        .upload(fileName, file);
      
      if (error) throw error;
      
      const { data: urlData } = supabase.storage
        .from('vendor-logos')
        .getPublicUrl(fileName);
      
      updateField('logo_url', urlData.publicUrl);
      toast({ title: 'Logo uploaded successfully' });
    } catch (error: any) {
      toast({ 
        title: 'Upload failed', 
        description: error.message,
        variant: 'destructive' 
      });
    } finally {
      setUploadingLogo(false);
    }
  };

  // Security questions management
  const addSecurityQuestion = () => {
    setFormData(prev => ({
      ...prev,
      security_questions: [...prev.security_questions, { question: '', answer: '' }]
    }));
  };

  const updateSecurityQuestion = (index: number, field: 'question' | 'answer', value: string) => {
    setFormData(prev => ({
      ...prev,
      security_questions: prev.security_questions.map((q, i) => 
        i === index ? { ...q, [field]: value } : q
      )
    }));
  };

  const removeSecurityQuestion = (index: number) => {
    setFormData(prev => ({
      ...prev,
      security_questions: prev.security_questions.filter((_, i) => i !== index)
    }));
  };

  // Handle save
  const handleSave = () => {
    if (!formData.vendor_name.trim()) {
      toast({ title: 'Vendor name is required', variant: 'destructive' });
      return;
    }
    
    // Generate vendor code if not provided
    const vendorCode = formData.vendor_code || 
      `VND-${formData.vendor_name.substring(0, 3).toUpperCase()}-${Date.now().toString(36).toUpperCase()}`;
    
    onSave({
      vendor_name: formData.vendor_name,
      vendor_code: vendorCode,
      portal_url: formData.portal_url || null,
      portal_username: formData.portal_username || null,
      portal_password_encrypted: formData.portal_password_encrypted || null,
      customer_service_phone: formData.customer_service_phone || null,
      customer_service_email: formData.customer_service_email || null,
      website_url: formData.website_url || null,
      logo_url: formData.logo_url || null,
      is_active: formData.is_active,
      notes: formData.notes || null,
      security_questions: formData.security_questions as unknown as any,
      service_regions: formData.service_regions as unknown as any,
      payment_methods: formData.payment_methods as unknown as any,
    } as any);
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(word => word[0]).join('').substring(0, 2).toUpperCase() || 'VN';
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            {vendor ? 'Edit Vendor' : 'Add New Vendor'}
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="basic" className="w-full">
          <TabsList className="grid w-full grid-cols-3 h-8">
            <TabsTrigger value="basic" className="text-xs">Basic Info</TabsTrigger>
            <TabsTrigger value="portal" className="text-xs">Portal Access</TabsTrigger>
            <TabsTrigger value="security" className="text-xs">Security</TabsTrigger>
          </TabsList>

          {/* Basic Info Tab */}
          <TabsContent value="basic" className="space-y-3 mt-3">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <Avatar className="h-14 w-14">
                <AvatarImage src={formData.logo_url} />
                <AvatarFallback className="bg-primary/10 text-primary">
                  {getInitials(formData.vendor_name)}
                </AvatarFallback>
              </Avatar>
              <div>
                <Label htmlFor="logo" className="text-xs cursor-pointer">
                  <div className="flex items-center gap-1.5 text-primary hover:underline">
                    <Upload className="h-3.5 w-3.5" />
                    {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                  </div>
                </Label>
                <input 
                  id="logo" 
                  type="file" 
                  accept="image/*" 
                  className="hidden" 
                  onChange={handleLogoUpload}
                  disabled={uploadingLogo}
                />
                <p className="text-[10px] text-muted-foreground">PNG, JPG up to 2MB</p>
              </div>
            </div>

            {/* Name and Code */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="vendor_name" className="text-xs">Vendor Name *</Label>
                <Input
                  id="vendor_name"
                  value={formData.vendor_name}
                  onChange={(e) => updateField('vendor_name', e.target.value)}
                  placeholder="e.g. Pacific Gas & Electric"
                  className="h-8 text-sm"
                />
              </div>
              <div>
                <Label htmlFor="vendor_code" className="text-xs">Vendor Code</Label>
                <Input
                  id="vendor_code"
                  value={formData.vendor_code}
                  onChange={(e) => updateField('vendor_code', e.target.value)}
                  placeholder="Auto-generated"
                  className="h-8 text-sm"
                />
              </div>
            </div>

            {/* Contact Info */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="phone" className="text-xs">Customer Service Phone</Label>
                <Input
                  id="phone"
                  value={formData.customer_service_phone}
                  onChange={(e) => updateField('customer_service_phone', e.target.value)}
                  placeholder="1-800-XXX-XXXX"
                  className="h-8 text-sm"
                />
              </div>
              <div>
                <Label htmlFor="email" className="text-xs">Customer Service Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.customer_service_email}
                  onChange={(e) => updateField('customer_service_email', e.target.value)}
                  placeholder="support@vendor.com"
                  className="h-8 text-sm"
                />
              </div>
            </div>

            {/* Website */}
            <div>
              <Label htmlFor="website" className="text-xs">Website URL</Label>
              <Input
                id="website"
                value={formData.website_url}
                onChange={(e) => updateField('website_url', e.target.value)}
                placeholder="https://vendor.com"
                className="h-8 text-sm"
              />
            </div>

            {/* Status */}
            <div className="flex items-center justify-between">
              <Label htmlFor="is_active" className="text-xs">Active Status</Label>
              <Switch
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) => updateField('is_active', checked)}
              />
            </div>

            {/* Notes */}
            <div>
              <Label htmlFor="notes" className="text-xs">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => updateField('notes', e.target.value)}
                placeholder="Additional notes about this vendor..."
                className="h-16 text-sm resize-none"
              />
            </div>
          </TabsContent>

          {/* Portal Access Tab */}
          <TabsContent value="portal" className="space-y-3 mt-3">
            <div>
              <Label htmlFor="portal_url" className="text-xs">Portal URL</Label>
              <Input
                id="portal_url"
                value={formData.portal_url}
                onChange={(e) => updateField('portal_url', e.target.value)}
                placeholder="https://portal.vendor.com"
                className="h-8 text-sm"
              />
            </div>

            <div>
              <Label htmlFor="portal_username" className="text-xs">Username</Label>
              <Input
                id="portal_username"
                value={formData.portal_username}
                onChange={(e) => updateField('portal_username', e.target.value)}
                placeholder="username@company.com"
                className="h-8 text-sm"
              />
            </div>

            <div>
              <Label htmlFor="portal_password" className="text-xs">Password</Label>
              <div className="relative">
                <Input
                  id="portal_password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.portal_password_encrypted}
                  onChange={(e) => updateField('portal_password_encrypted', e.target.value)}
                  placeholder="••••••••"
                  className="h-8 text-sm pr-9"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-muted rounded"
                >
                  {showPassword ? (
                    <EyeOff className="h-3.5 w-3.5 text-muted-foreground" />
                  ) : (
                    <Eye className="h-3.5 w-3.5 text-muted-foreground" />
                  )}
                </button>
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">
                Password is stored securely
              </p>
            </div>
          </TabsContent>

          {/* Security Tab */}
          <TabsContent value="security" className="space-y-3 mt-3">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-medium">Security Questions</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addSecurityQuestion}
                className="h-7 text-xs"
              >
                <Plus className="h-3 w-3 mr-1" />
                Add Question
              </Button>
            </div>

            {formData.security_questions.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-4">
                No security questions configured
              </p>
            ) : (
              <div className="space-y-3">
                {formData.security_questions.map((sq, index) => (
                  <div key={index} className="bg-muted/50 rounded-md p-2 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-medium text-muted-foreground">
                        Question {index + 1}
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeSecurityQuestion(index)}
                        className="h-6 w-6 p-0 text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                    <Input
                      value={sq.question}
                      onChange={(e) => updateSecurityQuestion(index, 'question', e.target.value)}
                      placeholder="Security question..."
                      className="h-7 text-xs"
                    />
                    <Input
                      value={sq.answer}
                      onChange={(e) => updateSecurityQuestion(index, 'answer', e.target.value)}
                      placeholder="Answer..."
                      className="h-7 text-xs"
                    />
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isLoading}>
            {isLoading ? 'Saving...' : (vendor ? 'Update Vendor' : 'Create Vendor')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
